import React from "react";
import { Link } from "react-router-dom";
import { FilePlus2, FolderSearch, FileOutput, Clock, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";

function StatCard({ label, value, hint, icon: Icon, testId }) {
  return (
    <Card data-testid={testId} className="relative overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm font-medium text-muted-foreground">
          {label}
          {Icon ? <Icon className="h-4 w-4 text-brand-gold" /> : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="font-mono text-3xl font-bold tabular-nums">{value}</div>
        {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const firstName = (user?.name || "").split(" ")[0] || "";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Tableau de bord
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Bienvenue{firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Commencez une nouvelle enquête budgétaire, ou reprenez une analyse
          en cours. Les finances publiques attendent d'être traduites en
          tranches de vies humaines.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Projets"
          value="0"
          hint="Créez votre premier projet d'audit"
          icon={FilePlus2}
          testId="stat-projects"
        />
        <StatCard
          label="Documents analysés"
          value="0"
          hint="PDF budgétaires extraits"
          icon={FolderSearch}
          testId="stat-documents"
        />
        <StatCard
          label="Rapports générés"
          value="0"
          hint="Exports PDF / Word / Excel"
          icon={FileOutput}
          testId="stat-reports"
        />
        <StatCard
          label="Jours de vie audités"
          value="—"
          hint="Disponible après la 1ʳᵉ analyse"
          icon={Clock}
          testId="stat-life-days"
        />
      </div>

      {/* CTA grid */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2 border-brand-gold/30" data-testid="cta-new-project">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FilePlus2 className="h-5 w-5 text-brand-gold" />
              Lancer une nouvelle analyse
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Le wizard en 5 étapes vous guide : contexte, profil contribuable,
              extraction automatique, revue, rapport final.
            </p>
            <div>
              <Link to="/app/new-project">
                <Button size="lg" data-testid="cta-new-project-btn">
                  Nouveau projet d'audit
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="cta-methodology">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-brand-gold" />
              Méthodologie vitaliste
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Formule centrale&nbsp;:
            </p>
            <pre className="mt-2 rounded-md bg-muted px-3 py-2 font-mono text-xs">
              J = P / (S / 30)
            </pre>
            <p className="mt-3 text-xs text-muted-foreground">
              <strong className="text-foreground">J</strong> : jours de vie ·
              <strong className="text-foreground"> P</strong> : prélèvements ·
              <strong className="text-foreground"> S</strong> : salaire mensuel.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Empty state */}
      <Card data-testid="empty-projects" className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-14 text-center">
          <div className="mb-4 rounded-full bg-muted p-4">
            <FilePlus2 className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold">Aucun projet pour l'instant</h3>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Votre tableau se remplira dès que vous créerez votre premier projet
            d'audit budgétaire.
          </p>
          <Link to="/app/new-project" className="mt-4">
            <Button data-testid="empty-projects-cta">Créer un projet</Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
